import React from 'react';
import { Link } from 'react-router-dom';
import Seo from '../components/Seo';
import { Reveal, MaskLine, Eyebrow } from '../components/Reveal';
import Marquee from '../components/Marquee';
import { servicePipeline, img, EMAIL } from '../data/content';
import { pageSeo, breadcrumbSchema } from '../data/seo';

const capabilities = [
  { id: 'facility', title: '2000 m² Production Facility', desc: 'Our Pune plant spans 2000 m² of manufacturing floor — machining, fabrication and assembly under one roof.', image: 'facility.jpg' },
  { id: 'software', title: 'Solid Edge · AutoCAD · Eplan', desc: 'Full in-house engineering on Solid Edge, AutoCAD and Eplan along with all associated design software.', image: 'robotic-sim.png' },
  { id: 'lab', title: 'In-House Cleanliness Testing Lab', desc: 'Gravimetric Millipore testing and particle size analysis verify every cleanliness claim we make.', image: 'ultrasonic-1.jpg' },
  { id: 'automation', title: 'Material Handling Automation', desc: 'Pick & place systems, conveyors, lifters, turn-over devices and turntables — complete line integration.', image: 'handling-2.jpg' }
];

const servicePoints = [
  'Experienced team for service and installation activity',
  'Adequate stock of the spare-wear parts at our office',
  'Maintenance & fault training delivered by our installation team',
  'Satisfactory installations at customers which in return give us repeat orders'
];

function AboutHero() {
  return (
    <section className="relative pt-40 pb-20 blueprint-grid overflow-hidden" data-testid="about-hero">
      <div className="absolute inset-0 bg-gradient-to-b from-ink/40 to-ink" aria-hidden="true" />
      <div className="mx-auto max-w-7xl px-5 lg:px-8 relative z-10">
        <Eyebrow className="mb-6 flex items-center gap-3">
          <span className="inline-block h-[1px] w-10 bg-flame" aria-hidden="true" /> About Tekwash Systems
        </Eyebrow>
        <h1 className="font-display font-extrabold text-paper leading-[0.98] tracking-tight text-4xl sm:text-5xl lg:text-6xl max-w-4xl">
          <MaskLine delay={0.15}>12+ YEARS OF</MaskLine>
          <MaskLine delay={0.3}>ENGINEERED</MaskLine>
          <MaskLine delay={0.45}><span className="text-flame">CLEANLINESS.</span></MaskLine>
        </h1>
        <p className="mt-8 max-w-2xl text-lg text-fog leading-relaxed">
          A leading state-of-the-art designer and manufacturer of industrial cleaning systems —
          from standard cleaning machines to fully automatic, high-pressure robotic, cutting-edge systems.
        </p>
      </div>
    </section>
  );
}

function Story() {
  return (
    <section className="py-20 lg:py-28" data-testid="about-story">
      <div className="mx-auto max-w-7xl px-5 lg:px-8 grid lg:grid-cols-2 gap-14 items-center">
        <Reveal>
          <div className="clip-notch border border-steelline/80 overflow-hidden">
            <img src={img('frontload-1.jpg')} alt="Tekwash machine in production" className="w-full aspect-[4/3] object-cover" />
          </div>
        </Reveal>
        <Reveal delay={0.15}>
          <Eyebrow className="mb-4">Who We Are</Eyebrow>
          <h2 className="font-display font-bold text-2xl sm:text-3xl text-paper tracking-tight">An all-in-one service provider.</h2>
          <p className="mt-6 text-fog leading-relaxed">
            Services include conceptualization, engineering, programming, production, installation & service —
            all from our production facility located in Pune, Maharashtra.
          </p>
          <p className="mt-4 text-fog leading-relaxed">
            Installations and clients are located all across India — primarily automobile, auto ancillary,
            general engineering, white goods and farm equipment companies.
          </p>
          <p className="mt-4 text-fog leading-relaxed">
            Along with washing machines, we also provide complete material handling automation —
            pick and place systems, conveyors, lifters, turn-over devices, turntables and more.
          </p>
          <Link to="/contact" data-testid="about-story-cta" className="mt-8 inline-flex bg-flame text-white font-semibold px-7 py-3.5 clip-notch hover:bg-flamedim transition-colors">
            Talk To Our Engineers
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

function Capabilities() {
  return (
    <section className="py-20 lg:py-28 bg-surface/40 border-y border-steelline/60" data-testid="about-capabilities">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <Reveal>
          <Eyebrow className="mb-4">Capabilities</Eyebrow>
          <h2 className="font-display font-extrabold text-2xl sm:text-3xl lg:text-4xl text-paper tracking-tight max-w-2xl">
            Everything a cleaning system needs, under one roof.
          </h2>
        </Reveal>
        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {capabilities.map((c, i) => (
            <Reveal key={c.id} delay={i * 0.1} testid={`capability-card-${c.id}`}>
              <div className="group h-full bg-ink border border-steelline/60 hover:border-flame/60 transition-colors duration-300">
                <div className="overflow-hidden">
                  <img src={img(c.image)} alt={c.title} className="w-full aspect-[4/3] object-cover group-hover:scale-105 transition-transform duration-700" />
                </div>
                <div className="p-6">
                  <h3 className="font-display font-bold text-lg text-paper">{c.title}</h3>
                  <p className="mt-3 text-sm text-fog leading-relaxed">{c.desc}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function Pipeline() {
  return (
    <section className="py-20 lg:py-28" data-testid="about-pipeline">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <Reveal>
          <Eyebrow className="mb-4">The Tekwash Pipeline</Eyebrow>
          <h2 className="font-display font-extrabold text-2xl sm:text-3xl lg:text-4xl text-paper tracking-tight max-w-2xl">
            Concept to commissioning, without a hand-off.
          </h2>
        </Reveal>
        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-steelline/50 border border-steelline/50">
          {servicePipeline.map((s, i) => (
            <Reveal key={s.step} delay={i * 0.08} className="bg-ink p-8 hover:bg-card transition-colors duration-300" testid={`pipeline-step-${s.step}`}>
              <div className="flex items-baseline gap-4">
                <span className="font-display font-extrabold text-3xl text-flame">{s.step}</span>
                <h3 className="font-display font-bold text-lg text-paper">{s.title}</h3>
              </div>
              <p className="mt-3 text-sm text-fog leading-relaxed">{s.desc}</p>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function ServiceSupport() {
  return (
    <section className="py-20 lg:py-28 bg-surface/40 border-t border-steelline/60" data-testid="about-service">
      <div className="mx-auto max-w-7xl px-5 lg:px-8 grid lg:grid-cols-2 gap-14 items-center">
        <Reveal>
          <Eyebrow className="mb-4">After-Sales Service Support</Eyebrow>
          <h2 className="font-display font-extrabold text-2xl sm:text-3xl text-paper tracking-tight">
            Monitored by our Directors. Repeated by our customers.
          </h2>
          <p className="mt-6 text-fog leading-relaxed">
            Continuous improvement over years in prompt after-sales service has resulted in repeat orders
            from all OEMs across India. Company's main focus is on service support — monitored by our Directors.
          </p>
          <ul className="mt-8 space-y-4">
            {servicePoints.map((p) => (
              <li key={p} className="flex gap-4 items-start">
                <span className="mt-1.5 h-2 w-2 bg-flame shrink-0" aria-hidden="true" />
                <span className="text-paper/90">{p}</span>
              </li>
            ))}
          </ul>
        </Reveal>
        <Reveal delay={0.2}>
          <div className="clip-notch border border-steelline/80 overflow-hidden">
            <img src={img('handling-1.jpg')} alt="Tekwash service and installation" className="w-full aspect-[3/4] object-cover" />
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function AboutCta() {
  return (
    <section className="py-24 text-center" data-testid="about-cta">
      <div className="mx-auto max-w-3xl px-5">
        <Reveal>
          <h2 className="font-display font-extrabold text-3xl sm:text-4xl text-paper tracking-tight">Build with us.</h2>
          <p className="mt-5 text-fog">Write to <a href={`mailto:${EMAIL}`} data-testid="about-email-link" className="text-flame hover:underline">{EMAIL}</a> and tell us about the components you need clean.</p>
        </Reveal>
      </div>
    </section>
  );
}

export default function About() {
  return (
    <>
      <Seo {...pageSeo.about} schemas={[breadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'About', path: '/about' }])]} />
      <AboutHero />
      <Story />
      <Capabilities />
      <Pipeline />
      <ServiceSupport />
      <Marquee />
      <AboutCta />
    </>
  );
}
